import {useMemo} from "react";
import {CircularProgress} from "@mui/material";
import {api} from "../../../../../api/API";
import {Opening} from "../../../../../api/Openings";
import {QuoteOpening} from "../../../../../api/QuoteOpenings";
import {
    AdjustCol,
    DistanceCol,
    NominalWidth,
    NumberCol,
    SeqNumber,
    StringCol,
    Table,
    sortStringInner, Column,
    lookupNestedProp,
} from "../../../../../misc/scroller/Table";
import {useProjectUnitSystem} from "../../ProjectName";
import {ChangePreviewContext} from "../../ShopDrawingChangePreview";
import {Checkbox2Col} from "../../ShopTableCol";
import {cellInActive} from "../../pricing/cellconst";
import {openingsTableName, StatusCol} from "../Openings";
import {PreviousValuePopup} from "../PreviousValuePopup";
import {
    DoorElevationCell,
    DoorElevationColumnHeader,
    DoorElevationFilter,
    renderDoorElevationStr,
} from "../DoorElevationHelpers";
import {
    AnchorCol,
    isWindow,
    JambDepthCol,
    PrepLocationCol,
} from "../../../quote/openings/QuoteOpenings";
import {WallTypeHeader} from "../../../quote/openings/WallTypeHeader";
import {ScreenElevationHeader} from "../../../quote/openings/ScreenElevationHeader";
import {deepOrange} from "@mui/material/colors";

export function OpeningsView(props: {
    project: number;
    tab: string;
    changeset?: number;
    openingIds: number[]
}) {
    const sys = useProjectUnitSystem();
    const {project, tab, changeset} = props;
    const previewCtx = useMemo(() => ({
        enabled: !!changeset,
        sessionId: changeset ?? 0,
    }), [changeset]);

    const showDoor = true;
    const showFrame = true;
    const tableName = openingsTableName(project, tab) + "_timeline";

    const proposing = !!changeset;

    if (!sys) return <CircularProgress />;

    return (
        <ChangePreviewContext.Provider value={previewCtx}>
            <Table<Opening>
                name={tableName}
                globalPrefsName={openingsTableName(0, tab) + "_timeline"}
                cellCustomize={{
                    backgroundColor: (row: Opening, c: Column<Opening>) => {
                        if(!proposing) {
                            return null;
                        }

                        let changed = false;

                        if("isChangedSinceBackup" in c && c.isChangedSinceBackup) {
                            changed = c.isChangedSinceBackup(row)
                        } else if(c.editKey) {
                            changed = !!row.backup && lookupNestedProp(row.backup, c.editKey) !== lookupNestedProp(row, c.editKey)
                        } else {
                            changed = false;
                        }

                        if(changed) {
                            if(row.logictrl?.conflictsWithMain) {
                                return deepOrange["100"];
                            }

                            return "white"
                        }

                        return cellInActive;
                    },
                    onFocus: (row: Opening, c: Column<Opening>, anchor: any) => {
                        if(!proposing) {
                            return null;
                        }

                        let changed = false;

                        if("isChangedSinceBackup" in c && c.isChangedSinceBackup) {
                            changed = c.isChangedSinceBackup(row)
                        } else if(c.editKey) {
                            changed = !!row.backup && lookupNestedProp(row.backup, c.editKey) !== lookupNestedProp(row, c.editKey)
                        } else {
                            changed = false;
                        }

                        if(changed) {
                            const value = row.backup as any;

                            return (
                                <PreviousValuePopup
                                    anchor={anchor} width={c.width}
                                    value={c.render(value, c)}
                                    hasConflict={row.logictrl?.conflictsWithMain}
                                />
                            )
                        }

                        return null;
                    }
                }}
                columns={[
                    SeqNumber<Opening>("seqNumber", tableName, async () => {}),
                    StringCol("Opening", "name"),
                    NumberCol("Qty", "qty"),
                    StringCol("Floor", "floor"),
                    Checkbox2Col("Ext", "exterior"),
                    StringCol("Location", "locationOne"),
                    StringCol<Opening>("To/From", "locationTransition"),
                    StringCol<Opening>("Location 2", "locationTwo"),
                    StringCol<Opening>("Type", "openingType"),
                    NominalWidth<Opening>("Nominal Width", sys, "nominalWidth", "inactiveDoorWidth"),
                    DistanceCol("Nominal Height", sys, "nominalHeight"),
                    StringCol<Opening>("Hand", "handing"),
                    StringCol<Opening>("Label", "label"),
                    showFrame && StringCol<Opening>("Frame Series", "frameSeries"),
                    showFrame && StringCol<Opening>("Frame Material", "frameMaterial"),
                    showFrame && AdjustCol(StringCol<Opening>("Frame Gauge", "frameGauge"), {
                        alignRight: true,
                    }),
                    showFrame && {
                        name: "Screen Elevation",
                        renderName: () => <ScreenElevationHeader />,
                        render: (data) => data.screenElevation || "",
                        filter: (data, search) => (data.screenElevation || "").toLowerCase().startsWith(search.toLowerCase()),
                        sort: (a, b) => sortStringInner(a.screenElevation || "", b.screenElevation || ""),
                    },
                    showFrame && StringCol<Opening>("Frame Profile", "frameProfile"),
                    showFrame && {
                        name: "Wall Type",
                        renderName: () => <WallTypeHeader />,
                        render: (data) => data.drawingWallLabel || "",
                        width: 100,
                        filter: (data, search) => (data.drawingWallLabel || "").toLowerCase().startsWith(search.toLowerCase()),
                        sort: (a, b) => sortStringInner(a.drawingWallLabel || "", b.drawingWallLabel || ""),
                    },
                    showFrame && JambDepthCol<Opening>("Jamb Depth", "jambDepth"),
                    showFrame && StringCol<Opening>("Frame Construction", "frameConstruction"),
                    showFrame && AnchorCol<Opening>(),
                    showDoor && AdjustCol(StringCol<Opening>("Door Series", "doorSeries"), {
                        disabled: isWindow as any,
                    }),
                    showDoor && AdjustCol(StringCol<Opening>("Door Material", "doorMaterial"), {
                        disabled: isWindow as any,
                    }),
                    showDoor && AdjustCol(StringCol<Opening>("Door Finish", "doorFinish"), {
                        disabled: isWindow as any,
                    }),
                    showDoor && AdjustCol(StringCol<Opening>("Door Gauge", "doorGauge"), {
                        alignRight: true,
                    }),
                    showDoor && AdjustCol(StringCol<Opening>("Door Thickness", "doorThickness"), {
                        disabled: isWindow as any,
                    }),
                    showDoor && AdjustCol<QuoteOpening>({
                        name: "Door Elevation",
                        width: 200,
                        renderName: () => <DoorElevationColumnHeader />,
                        renderFilter: (p) => <DoorElevationFilter {...p} />,
                        render: (data) => <DoorElevationCell data={data} />,
                        filter: (data, search) => renderDoorElevationStr(data, false).toLowerCase().startsWith(search.toLowerCase()),
                        sort: (a, b) => sortStringInner(renderDoorElevationStr(a, false), renderDoorElevationStr(b, false)),
                    }, {
                        disabled: isWindow as any,
                    }),
                    showDoor && AdjustCol(StringCol<Opening>("Door Core", "doorCore"), {
                        disabled: isWindow as any,
                    }),
                    showDoor && AdjustCol(StringCol<Opening>("Door Seam", "doorSeam"), {
                        disabled: isWindow as any,
                    }),
                    PrepLocationCol<Opening>(),
                    {
                        name: "Hardware Heading",
                        render: (data) => data.hardwareGroupName || "",
                        filter: (data, search) => data.hardwareGroupName?.indexOf(search) === 0,
                        isChangedSinceBackup: (data) => data.hardwareGroupName !== data.backup?.hardwareGroupName,
                    },
                    {
                        name: "HW Shortcode",
                        width: 150,
                        isChangedSinceBackup: (data) => data.hardwareGroupShortcodeName !== data.backup?.hardwareGroupShortcodeName,
                        render: (data) => data.hardwareGroupShortcodeName || "",
                        filter: (data, search) => !!data.hardwareGroupShortcodeName && data.hardwareGroupShortcodeName.toLowerCase().indexOf(search.toLowerCase()) !== -1,
                        sort: (a, b) => sortStringInner(a.hardwareGroupShortcodeName || "", b.hardwareGroupShortcodeName || ""),
                    },
                    showDoor && StatusCol<Opening>("Door Status", "doorStatus"),
                    showFrame && StatusCol<Opening>("Frame Status", "frameStatus"),
                    StringCol("Notes", "notes", 300),
                ]}
                fetch={async (ctx) => {
                    return api.openings.getByIds({
                        project,
                        ids: props.openingIds,
                        previewChangeset: changeset,
                    })
                }}
                fetchDeps={[project, changeset]}
            />
        </ChangePreviewContext.Provider>
    );
}
